import { Footer, LanguageSelector, LoadingSpinner } from '@web_opxp/components';
import {
  AuthSelectors,
  CookieConsent,
  ELanguage,
  FirebaseActions,
  InAppMessage,
  LanguageActions,
  LanguageSelectors,
  MessagingController,
  useTranslation,
} from '@web_opxp/features';
import React, { lazy, useMemo } from 'react';
import { connect } from 'react-redux';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import './Root.scss';
import { AppSelectors } from './app';
import { AssessmentCfqRoute, AssessmentEvaluationRoute, AssessmentRoute, TrainingIntensityRoute } from './assessment';
import {
  PeerGroupSelectionRoute,
  SpecialNeedsRoute,
  TrainingLedRoute,
  TrainingScheduleRoute,
} from './assessment/AssessmentPage';
import { CFQSubpage } from './assessment/CFQ';
import EvaluationSubpage from './assessment/Evaluation/EvaluationSubpage';
import PeerGroupSection from './assessment/PeerGroup/PeerGroupSection';
import SpecialNeedsSubpage from './assessment/SpecialNeeds/SpecialNeedsSubpage';
import TrainingLedSubpage from './assessment/TrainingLed/TrainingLedSubpage';
import TrainingScheduleSubpage from './assessment/TrainingSchedule/TrainingScheduleSubpage';
import { TRAINING_AVAILABLE_LANGUAGES } from './base/constants';
import ENV from './base/env';
import { getFooterMenu, getFooterSocialItems, getLegalLinks } from './base/footer';
import { getInternalUrl, getPrivacyPolicyUrl } from './base/historyService';
import Bridge, { BridgeSelectors } from './bridge';
import HeaderWrapper from './components/header/HeaderWrapper';
import Dashboard, { DashboardRoute } from './dashboard/Dashboard';
import { EvaluationRoute } from './evaluation';
import { AchievementsRoute } from './evaluation/Achievements';
import { ExerciseRoute } from './exercise/Exercise';
import { ExercisesRoute } from './exercises';
import MedBanner from './medBanner';
import { MigrationRoute } from './migration';
import { PersonalizationRoute } from './personalization';
import { IState } from './store/state';
import { TrainingIntensityPage } from './trainingIntensity';

export interface IRootProps {
  bridgeActive: boolean;
  language: ELanguage | undefined;
  loading: boolean;
  session?: string;
  onLanguageChange: (lang: ELanguage) => void;
  onToggleCookieSettings: () => void;
}

const Exercise = lazy(() => import('./exercise/Exercise'));
const Exercises = lazy(() => import('./exercises/Exercises'));
const Evaluation = lazy(() => import('./evaluation/Evaluation'));
const Achievements = lazy(() => import('./evaluation/Achievements'));
const Personalization = lazy(() => import('./personalization/Personalization'));
const AssessmentPage = lazy(() => import('./assessment/AssessmentPage'));
const ConnectedFirebaseMessaging = lazy(() => import('./playground/FirebaseMessaging'));
const MigrationPage = lazy(() => import('./migration/Migration'));
const NeuroBoostersPage = lazy(() => import('./neuroboosters'));
const WelcomeTourPage = lazy(() => import('./welcomeTour/WelcomeTourPage'));

export const RootView: React.FC<IRootProps> = ({
  bridgeActive,
  language,
  loading,
  session,
  onLanguageChange,
  onToggleCookieSettings,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const langSelector = useMemo(() => {
    return (
      <LanguageSelector
        availableLanguages={TRAINING_AVAILABLE_LANGUAGES}
        current={language}
        onSelect={(lang: string) => onLanguageChange(lang as ELanguage)}
      />
    );
  }, [language, onLanguageChange]);

  if (loading || !language) return <LoadingSpinner assetsUrl={ENV.AssetsBaseUrl} />;
  const appBodyClass = `App-body${bridgeActive ? ' hidden' : ''}`;
  const privacyUrl = !language ? '' : getPrivacyPolicyUrl(language);

  return (
    <div className="App">
      <MessagingController getInternalUrl={getInternalUrl} />
      <MedBanner />
      <InAppMessage page={location.pathname} />
      <HeaderWrapper />
      <div className={appBodyClass}>
        <Switch>
          <Route path={ExerciseRoute}>
            <Exercise />
          </Route>
          <Route path={DashboardRoute}>
            <Dashboard />
          </Route>
          <Route path={ExercisesRoute}>
            <Exercises />
          </Route>
          <Route path={`${EvaluationRoute}/:userId?`}>
            <Evaluation />
          </Route>
          <Route path={AchievementsRoute}>
            <Achievements />
          </Route>
          <Route path={`${PersonalizationRoute}/:userId?`}>
            <Personalization />
          </Route>
          <Route path={`${AssessmentEvaluationRoute}`}>
            <EvaluationSubpage />
          </Route>
          <Route path={`${AssessmentCfqRoute}`}>
            <CFQSubpage />
          </Route>
          <Route path={`${TrainingScheduleRoute}`}>
            <TrainingScheduleSubpage />
          </Route>
          <Route path={`${PeerGroupSelectionRoute}`}>
            <PeerGroupSection />
          </Route>
          <Route path={`${SpecialNeedsRoute}`}>
            <SpecialNeedsSubpage />
          </Route>
          <Route path={`${TrainingLedRoute}`}>
            <TrainingLedSubpage />
          </Route>
          <Route path={`${TrainingIntensityRoute}`}>
            <TrainingIntensityPage />
          </Route>
          <Route path={AssessmentRoute}>
            <AssessmentPage />
          </Route>
          <Route path="/playground">
            <ConnectedFirebaseMessaging />
          </Route>
          <Route path={MigrationRoute}>
            <MigrationPage />
          </Route>
          <Route path="/extras">
            <NeuroBoostersPage />
          </Route>
          <Route path="/welcome_tour">
            <WelcomeTourPage />
          </Route>
          <Redirect exact path="/" to={DashboardRoute} />
          {/* <Route component={NotFoundPage} /> */}
        </Switch>
      </div>
      <Bridge language={language} />
      <Footer
        languageSelector={langSelector}
        copyrightLine={t('common.common:Footer.Copyright') + ' | ' + t('common.common:Footer.ContactLine')}
        footerMenuItems={getFooterMenu(session)}
        socialBarItems={getFooterSocialItems(false)}
        legalLinks={getLegalLinks()}
        onToggleCookieSettings={onToggleCookieSettings}
        assetBaseUrl={ENV.AssetsBaseUrl}
        cookieLinkTitle={t('common.common:Cookies.FooterLink') ?? ''}
      />
      <CookieConsent privacyUrl={privacyUrl} />
    </div>
  );
};

export const mapStateToProps = (state: IState) => {
  const bridgeActive = BridgeSelectors.isBridgeActive(state);
  const language = LanguageSelectors.getLanguage(state);
  const loading = AppSelectors.isAppLoading(state);
  const session = AuthSelectors.getToken(state);
  return {
    bridgeActive,
    language,
    loading,
    session,
  };
};

export const mapDispatchToProps = {
  onLanguageChange: LanguageActions.changeLanguage,
  onToggleCookieSettings: FirebaseActions.toggleSettingsAction,
};

const Root = connect(mapStateToProps, mapDispatchToProps)(RootView);
export default Root;
